import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '../components/GlassCard';
import { useUserStore } from '../store/userStore';
import { getReleasedBiomarkers } from '../lib/api';

const FLAG_STYLE = {
  low: { label: 'Low', color: '#F59E0B' },
  high: { label: 'High', color: '#EF4444' },
};

const fmtDate = (d) => {
  try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; }
};

export default function BiomarkerInput() {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const [results, setResults] = useState([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    getReleasedBiomarkers(user.id)
      .then((r) => { setResults(r?.results || []); setPending(r?.pending_count || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.id]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--aura-bg)' }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate('/app')} className="grid place-items-center w-9 h-9 rounded-full bg-white border border-[var(--aura-line)]"><i className="fa-solid fa-arrow-left" /></button>
          <h1 className="font-display text-2xl sm:text-3xl">My blood results</h1>
        </div>
        <p className="text-[var(--aura-ink-soft)] mb-6 ml-12">Results are added by your clinical team. You can view them here but not change them.</p>

        <GlassCard className="p-4 mb-5 flex items-start gap-3 border border-[var(--aura-purple)]/20" data-testid="biomarker-readonly-notice">
          <div className="grid place-items-center w-9 h-9 rounded-xl bg-[var(--aura-purple)]/10 text-[var(--aura-purple)] shrink-0"><i className="fa-solid fa-lock" /></div>
          <p className="text-sm text-[var(--aura-ink-soft)]">Think a value looks wrong? Message your clinician and they can correct it for you.</p>
        </GlassCard>

        {pending > 0 ? (
          <GlassCard className="p-4 mb-5 flex items-start gap-3 border border-amber-300/40 bg-amber-50/60" data-testid="biomarker-pending-notice">
            <div className="grid place-items-center w-9 h-9 rounded-xl bg-amber-100 text-amber-700 shrink-0"><i className="fa-solid fa-stethoscope" /></div>
            <p className="text-sm text-[var(--aura-ink-soft)]">
              {pending === 1 ? 'A new set of results is' : `${pending} new sets of results are`} being reviewed by your clinician. They'll appear here once released — your clinician may get in touch to talk them through first.
            </p>
          </GlassCard>
        ) : null}

        {loading ? (
          <div className="grid place-items-center py-16"><i className="fa-solid fa-circle-notch fa-spin text-2xl text-[var(--aura-purple)]" /></div>
        ) : results.length === 0 ? (
          <GlassCard className="p-10 text-center" data-testid="biomarker-empty">
            <div className="grid place-items-center w-14 h-14 rounded-2xl aura-btn-gradient text-white mx-auto mb-4"><i className="fa-solid fa-vial text-xl" /></div>
            <h2 className="font-display text-2xl mb-2">{pending > 0 ? 'Results under review' : 'No results yet'}</h2>
            <p className="text-[var(--aura-ink-soft)]">{pending > 0 ? 'Your clinician is reviewing your results and will release them here shortly.' : 'Once your clinician uploads and releases your blood test, it will appear here along with an AI analysis.'}</p>
          </GlassCard>
        ) : (
          <>
            <button
              data-testid="view-analysis-btn"
              onClick={() => navigate('/biomarkers/analysis')}
              className="w-full mb-5 aura-btn-gradient text-white font-semibold rounded-2xl py-3.5 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-microscope" /> View AI analysis
            </button>
            {results.map((r, idx) => (
              <GlassCard key={r.id || idx} className="p-5 mb-4" data-testid={`biomarker-result-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-display text-lg">{fmtDate(r.test_date)}</div>
                    <div className="text-xs text-[var(--aura-ink-soft)]">
                      {r.released_by_name ? <>Released by {r.released_by_name}</> : 'Released by your clinical team'}
                      {r.lab_name ? <> · {r.lab_name}</> : null}
                    </div>
                  </div>
                  {r.flags?.length > 0 ? (
                    <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-amber-50 text-amber-700">{r.flags.length} flagged</span>
                  ) : (
                    <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700">All in range</span>
                  )}
                </div>
                {r.patient_note ? (
                  <div className="mb-3 rounded-xl bg-[var(--aura-purple)]/8 border border-[var(--aura-purple)]/20 px-3 py-2.5 text-sm" data-testid={`biomarker-note-${idx}`}>
                    <div className="text-[11px] uppercase tracking-wide text-[var(--aura-purple)] font-semibold mb-0.5"><i className="fa-solid fa-comment-medical mr-1" />Note from your clinician</div>
                    <p className="text-[var(--aura-ink)]">{r.patient_note}</p>
                  </div>
                ) : null}
                <div className="grid sm:grid-cols-2 gap-2">
                  {Object.entries(r.results || {}).map(([key, m]) => {
                    const flag = m?.flag ? FLAG_STYLE[m.flag] : null;
                    return (
                      <div key={key} className="flex items-center justify-between rounded-xl border border-[var(--aura-line)] bg-white/60 px-3 py-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{m?.name || key}</div>
                          {m?.reference_range ? <div className="text-[11px] text-[var(--aura-ink-soft)]">Range {m.reference_range} {m.unit}</div> : null}
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <div className="font-semibold" style={{ color: flag ? flag.color : 'inherit' }}>{m?.value} <span className="text-xs font-normal text-[var(--aura-ink-soft)]">{m?.unit}</span></div>
                          {flag ? <div className="text-[11px] font-semibold" style={{ color: flag.color }}>{flag.label}</div> : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </GlassCard>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
